import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { Link, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useRequestData, useLabels } from 'wsm-common-data';
import { trackEvent } from 'ddc-track-event';
import {
	setYear,
	setModel,
	setImage,
	setPrice,
	setBodyStyle,
	setVehicleConfiguration
} from '../actions/setVehicleConfiguration';
import { setFranchiseModels } from '../actions/setAccountData';
import { formatter } from '../helpers';
import { API } from '../api/services';
import stepController from '../controllers/StepController';

const Model = ({ setCurrentStep }) => {
	const labels = useLabels();
	const { accountData, vehicleConfiguration, rootDir } = useSelector(
		(state) => state
	);
	const { make: makeFromParams } = useParams();
	const make =
		vehicleConfiguration.make.toLowerCase() || makeFromParams.toLowerCase();

	const { franchiseModels, accountId } = accountData;
	const modelsForMake = franchiseModels[make.toLowerCase()];
	const { data: modelsObj, hasDisplayData } = modelsForMake;
	const modelsList = Object.values(modelsObj);

	const dispatch = useDispatch();
	const { locale, deviceType, widgetName, windowId } = useRequestData();

	const addBorderCls = deviceType === 'MOBILE' ? '' : 'border-bottom';

	const handleSetModel = (car) => {
		const { year, model, imageUrl, startingAtPrice, bodyStyle } = car;
		dispatch(setYear(year));
		dispatch(setModel(model));
		dispatch(setImage(imageUrl));
		dispatch(setPrice(startingAtPrice));
		dispatch(setBodyStyle(bodyStyle));
		trackEvent(widgetName, windowId, {
			element: 'Model option',
			elementCTA: model,
			action: 'clicked',
			result: 'Model selected updated'
		});
		stepController.updateLsVehicleConfig();
	};

	// TEMP UNTIL WE SOLVE MODEL DATA ISSUE
	const placeHolderImgUrl =
		'/static/v8/global/images/bodystyle/160x87/sedan_ext_225.png';

	useEffect(() => {
		setCurrentStep('model');
		stepController.updateLsVehicleConfig();
	}, [setCurrentStep]);

	useEffect(() => {
		const lsVehicleConfig = stepController.getLsVehicleConfig();
		// when you reload model screen with existing model
		if (
			!vehicleConfiguration.make &&
			lsVehicleConfig &&
			lsVehicleConfig.make === make
		) {
			dispatch(setVehicleConfiguration(lsVehicleConfig));
		} else if (!vehicleConfiguration.make) {
			// when you reload model screen with another model
			const newVehicleConfig = { ...vehicleConfiguration, make };
			dispatch(setVehicleConfiguration(newVehicleConfig));
			stepController.updateLsVehicleConfig();
		}
	}, []);

	useEffect(() => {
		if (!hasDisplayData) {
			const options = {
				limit: 1,
				keepIndex: true,
				fields: ['images', 'startingAtPrice', 'globalVehicleId']
			};

			const newModelsState = { ...modelsObj };
			// sends these requests in chunks of 10
			const getGvModelsSync = async () => {
				let resolvedModelsData = {};
				const modelChunkSize = 10;
				let start = 0;
				let end =
					modelsList.length > modelChunkSize
						? modelChunkSize
						: modelsList.length;

				/* eslint-disable no-await-in-loop */
				while (start < end) {
					const modelsChunk = modelsList.slice(start, end);
					const modelData = await API.getGvModels(
						modelsChunk,
						make,
						accountId,
						locale,
						options
					);

					resolvedModelsData = {
						...resolvedModelsData,
						...modelData
					};

					start += modelChunkSize;
					end =
						end > modelsList.length
							? modelsList.length
							: start + modelChunkSize;
				}

				/* WIP UPDATE MODELS STATE WITH RESOLVED DATA */
				Object.keys(newModelsState).forEach((modelKey) => {
					const modelData = resolvedModelsData[modelKey][0];
					const { imageUrl, startingAtPrice, globalVehicleId } =
						modelData;

					newModelsState[modelKey] = {
						...newModelsState[modelKey],
						startingAtPrice,
						imageUrl,
						globalVehicleId
					};
				});

				if (Object.keys(newModelsState).length) {
					dispatch(
						setFranchiseModels({
							...franchiseModels,
							[make]: {
								data: newModelsState,
								hasDisplayData: true
							}
						})
					);
				}
			};

			getGvModelsSync();
		}
	}, [accountId, locale, make, modelsList]);

	const getImageUrl = (car) => {
		let carImageUrl = placeHolderImgUrl;
		if (car.imageUrl) {
			carImageUrl = `${car.imageUrl.replace(
				'origin-images.qa.',
				'images.'
			)}?impolicy=resize&w=200`;
		}
		return carImageUrl;
	};

	const isLoading = !(modelsList?.length && modelsList?.[0]?.startingAtPrice);
	const containerLoadingClass = isLoading ? 'car-customizer-loading' : '';

	return (
		// This extra div is to prevent the refresh/direct url render from
		// not applying the correct css classes to the parent container of the buttons
		<div>
			<div className="m-auto">
				<div
					className={`car-customizer-flex-container pb-4 mb-4 ${addBorderCls} ${containerLoadingClass}`}
				>
					{modelsList.length ? (
						modelsList.map((car) =>
							!isLoading ? (
								<div
									className="box box-border m-3 p-4 bg-white rounded box-selection car-customizer-flex-item"
									style={{
										fontWeight: 400,
										textDecoration: 'none',
										minHeight: '12em'
									}}
									key={`model_button_${car.model}`}
								>
									<Link
										onClick={() => handleSetModel(car)}
										to={`/${rootDir}/${make}/${car.model}.htm`}
										key={`model_button_${car.model}`}
										className="d-flex justify-content-between flex-column h-100"
									>
										<div className="d-flex flex-column justify-content-around h-100 px-3">
											<img
												src={getImageUrl(car)}
												alt="carImage"
											/>
										</div>
										<div>
											<div style={{ lineHeight: 1 }}>
												{car.model}
											</div>
											{car.startingAtPrice && (
												<span className="ddc-font-size-small">
													{labels.get('STARTING_AT')}{' '}
													{formatter.format(
														car.startingAtPrice
													)}
												</span>
											)}
										</div>
									</Link>
								</div>
							) : (
								<div
									className="skeleton-shimmer box box-border m-3 p-2 bg-white rounded box-selection car-customizer-flex-item"
									style={{
										fontWeight: 400,
										textDecoration: 'none',
										minHeight: '12em'
									}}
									key={`${car.model}_shimmer_image`}
								>
									<div className="d-flex justify-content-between flex-column h-100">
										<div className="h-100 w-100" />
									</div>
								</div>
							)
						)
					) : (
						<div>{labels.get('NO_MODELS_AVAILABLE')}</div>
					)}
				</div>
			</div>
		</div>
	);
};

Model.propTypes = {
	setCurrentStep: PropTypes.func
};

export default Model;
